import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Link from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import modifyUpgradeToBusniessAccountModalOpenState from "@WhiteCap/Handlers/SubscriptionsDashboard/ShowUpgradeToBusinessAccount";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    destination = "destination",
}

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        isUpgradeToBusinessAccountModalOpen:
            state.WhiteCap.subscriptionsDashboardData.isUpgradeToBusinessAccountModalOpen,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapDispatchToProps = {
    modifyUpgradeToBusniessAccountModalOpenState,
};

interface OwnProps extends Props {
    fields: {
        [fields.destination]: LinkFieldValue;
    };
}

export interface UpgradeToBusinessAccountModalStyles {
    modal?: ModalPresentationProps;
    title?: TypographyProps;
    text?: TypographyProps;
    subTitle?: TypographyProps;
    footer?: InjectableCss;
    body?: InjectableCss;
}

export const upgradeToBusinessAccountModalStyles: UpgradeToBusinessAccountModalStyles = {
    modal: {
        size: 944,
        cssOverrides: {
            modalTitle: css`
                border-bottom: none;
                align-items: unset;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                                padding: 56px 20px 0 20px;
                            `,
                            null,
                            css`
                                padding: 40px 39px 0 52px;
                            `,
                        ],
                        "min",
                    );
                }}
                [class^="TypographyStyle"] {
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    font-size: 14px;
                                    font-weight: 450;
                                    line-height: 20px;
                                `,
                                null,
                                css`
                                    font-size: 18px;
                                    font-weight: 450;
                                    line-height: 40px;
                                `,
                            ],
                            "min",
                        );
                    }}
                }
                [class^="ButtonWrapper"] {
                    max-height: 36px;

                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    margin-top: -40px;
                                `,
                                null,
                                css`
                                    margin-top: -10px;
                                `,
                            ],
                            "min",
                        );
                    }}
                }
            `,
            modalContent: css`
                padding: 0;
                display: flex;
                flex-direction: column;
                height: auto;
                flex-grow: 1;
                overflow: hidden;
            `,
            modalContainer: css`
                [class^="ContentBody"] {
                    overflow: hidden;
                    height: auto;
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    margin-left: 8px;
                                    margin-right: 8px;
                                `,
                                null,
                                css``,
                            ],
                            "max",
                        );
                    }}
                }
            `,
        },
    },
    title: {
        variant: "h2",
        css: css`
            margin-bottom: 3rem;
        `,
    },
    text: {
        variant: "p",
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 12px;
                            line-height: 20px;
                            margin-bottom: 1rem;
                        `,

                        css`
                            font-size: 16px;
                            line-height: 30px;
                            margin-bottom: 1.5rem;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
    subTitle: {
        variant: "h2",
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 12px;
                            line-height: 20px;
                            font-weight: 450;
                            margin-bottom: 0.5rem;
                        `,

                        css`
                            font-size: 16px;
                            line-height: 30px;
                            font-weight: 450;
                            margin-bottom: 0.8rem;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
    footer: {
        css: css`
            display: flex;
            gap: 20px;
            padding: 15px;
            justify-content: center;
            align-items: center;
        `,
    },
    body: {
        css: css`
            flex-grow: 1;
            overflow: auto;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 30px 20px 0 20px;
                        `,

                        css`
                            padding: 40px 40px 0 52px;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
};

const styles = upgradeToBusinessAccountModalStyles;

const UpgradeToBusinessAccountModal: FC<OwnProps> = ({
    isUpgradeToBusinessAccountModalOpen,
    modifyUpgradeToBusniessAccountModalOpenState,
    fields: { destination },
}) => {
    const { url } = useGetLink(destination);

    const modalCloseHandler = () => {
        modifyUpgradeToBusniessAccountModalOpenState({
            isOpen: false,
        });
    };

    if(!isUpgradeToBusinessAccountModalOpen){ return null;}
    return (
        <Modal
            {...styles.modal}
            isOpen={isUpgradeToBusinessAccountModalOpen}
            handleClose={modalCloseHandler}
            headline="To manage your Order Status Notifications, you'll need to upgrade to Business Account"
        >
            <StyledWrapper {...styles.body}>
                <Typography {...styles.text}>
                    {translate(
                        "Take advantage of becoming a business account customer and enjoy the following benefits:",
                    )}
                    &nbsp;
                </Typography>

                <Typography {...styles.subTitle}>{translate("Cost Savings")}</Typography>

                <Typography {...styles.text}>
                    {translate(
                        "Joining as a business account holder means access to exclusive discounts and promotions that result in significant cost savings on your purchases",
                    )}
                </Typography>

                <Typography {...styles.subTitle}>{translate("Customized Offers")}</Typography>

                <Typography {...styles.text}>
                    {translate("Receive tailored offers and solutions that align with your business goals")}
                </Typography>

                <Typography {...styles.subTitle}>{translate("Quick and Easy Support")}</Typography>

                <Typography {...styles.text}>
                    {translate(
                        "As a valued business account customer, enjoy priority support and streamlined processes for quicker and more efficient transactions",
                    )}{" "}
                </Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.footer}>
                <Button variant="secondary" onClick={() => modalCloseHandler()}>
                    {translate("CANCEL")}
                </Button>
                <Link href={url}>
                    <Button variant="primary">{translate("PROCEED")}</Button>
                </Link>
            </StyledWrapper>
        </Modal>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(UpgradeToBusinessAccountModal),
    definition: {
        group: "Common",
        displayName: "Upgrade to Business Account Modal",
        fieldDefinitions: [
            {
                name: fields.destination,
                displayName: "Proceed Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Contextual",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
